import { useRef, useState } from 'react';
import { useAttachmentsFormContext } from '../EmployeeDocumentsSection';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import {
  deleteFetcher,
  getFetcher,
  postFetcher,
} from '@/api/fetchers';
import { notifications } from '@mantine/notifications';
import {
  Card,
  CheckIcon,
  Flex,
  Group,
  Table,
  UnstyledButton,
  Text,
  rem,
  Select,
  Menu,
  MenuItem,
  ScrollArea,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import dayjs from 'dayjs';
import { dateFormat } from '@/index';
import { type EmployeeAttachment } from '@/schemas/API/GET/employee/attachment/response';
import { displayError } from '@/utils/error/errorHandler';
import { subPath } from '@/subpath.config';
import { downloadFileWithBearerToken } from '@/utils/functions';

interface FilterOption {
  value: string;
  label: string;
}

export const AttachmentCard: React.FC = () => {
  const form = useAttachmentsFormContext();
  const [dateFilter, setDateFilter] = useState('');
  const [fileTypeFilter, setFileTypeFilter] = useState('vse');
  const { employeeId } = useParams<{ employeeId: string }>();
  const [selectedFileType, setFileType] = useState('OTHER');

  const { data: requestedOptions } = useSWR<Record<string, string>>(
    `/mappings/attachments`,
    getFetcher
  );

  const fileOptions: FilterOption[] = requestedOptions
    ? Object.entries(requestedOptions).map(([key, value]) => {
        return { value: key, label: value };
      })
    : [];

  let filterByFileTypeOptions: FilterOption[] = [];
  if (requestedOptions)
    filterByFileTypeOptions = Object.entries(requestedOptions).map(
      ([value, label]) => ({
        value,
        label,
      })
    );
  else {
    filterByFileTypeOptions = [
      {
        value: 'vse',
        label: 'Vše',
      },
    ];
  }

  const filterByDateOptions = [
    { value: 'newest', label: 'Nejnovější' },
    { value: 'oldest', label: 'Nejstarší' },
  ];

  const handleDelete = async (removeId: number): Promise<void> => {
    let index = -1;
    for (let i = 0; i < Object.keys(form.values.attachments).length; i++)
      if (form.values.attachments[i].id === removeId) {
        index = i;
        break;
      }
    if (index < 0) return;

    try {
      await deleteFetcher(`/employees/${employeeId}/attachments/${removeId}`);
      form.removeListItem('attachments', index);

      notifications.show({
        message: 'Příloha byla úspěšně smazána.',
        color: 'green',
        icon: <CheckIcon style={{ width: rem(20), height: rem(20) }} />,
      });
    } catch (err) {
      displayError(err);
    }
  };

  function showConfirmModal(id: number): void {
    modals.openConfirmModal({
      title: 'Smazat přílohu',
      centered: true,
      children: <div>Určitě chcete přílohu smazat? Tato akce je nevratná.</div>,
      labels: { confirm: 'Smazat', cancel: 'Zrušit' },
      confirmProps: { color: 'red' },
      onCancel: () => {},
      onConfirm: () => {
        void handleDelete(id);
      },
    });
  }

  function formatDate(inputDate: string | null): string {
    if (inputDate === null) return '';
    const parsedDate = new Date(inputDate.toString());
    const day = parsedDate.getUTCDate().toString().padStart(2, '0');
    const month = (parsedDate.getUTCMonth() + 1).toString().padStart(2, '0');
    const year = parsedDate.getUTCFullYear();
    return `${day}.${month}.${year}`;
  }

  function showTable(): JSX.Element {
    const attachments: EmployeeAttachment[] = [];
    for (let i = 0; i < Object.keys(form.values.attachments).length; i++)
      if (
        fileTypeFilter === 'vse' ||
        form.values.attachments[i].type === fileTypeFilter
      )
        attachments.push(form.values.attachments[i]);

    if (dateFilter !== '')
      attachments.sort((a, b) =>
        dateFilter === 'newest'
          ? b.createdAt.toString().localeCompare(a.createdAt.toString())
          : a.createdAt.toString().localeCompare(b.createdAt.toString())
      );

    const downloadSvgPath = `${subPath}/icons/download.svg`;
    const removeSvgPath = `${subPath}/icons/remove.svg`;
    return (
      <Table
        verticalSpacing="md"
        style={{
          fontSize: 'medium',
        }}
      >
        <Table.Tbody>
          {attachments.map(attachment => (
            <Table.Tr key={attachment.id}>
              <Table.Td className="text-left">{attachment.name}</Table.Td>
              <Table.Td className="text-right">
                <Flex gap="md" justify="flex-end">
                  {formatDate(attachment.contract.signDate)}
                  <UnstyledButton
                    style={{ backgroundColor: 'transparent' }}
                    onClick={async () => {
                      await downloadFileWithBearerToken(attachment.url);
                    }}
                  >
                    <img src={downloadSvgPath} alt="download icon"></img>
                  </UnstyledButton>
                  <UnstyledButton
                    style={{ backgroundColor: 'transparent' }}
                    onClick={() => {
                      showConfirmModal(attachment.id);
                    }}
                  >
                    <img src={removeSvgPath} alt="trash icon"></img>
                  </UnstyledButton>
                </Flex>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    );
  }

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = (): void => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const file = event.target.files?.[0];
    if (file) {
      void handleFilePost(file);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFilePost = async (file: File): Promise<void> => {
    try {
      const formData = new FormData();
      formData.append('Content-Type', 'multipart/form-data');
      formData.append('name', file.name);
      formData.append('expirationDate', dayjs().format(dateFormat));
      formData.append('signatureDate', dayjs().format(dateFormat));
      formData.append('serviceId', '');
      formData.append('file', file);
      formData.append('type', selectedFileType);
      formData.append('Accept', 'application/json');

      const newAttachment = await postFetcher(
        `/employees/${employeeId}/attachments`,
        formData
      );
      if (
        form.values.attachments.map(attachment => {
          return attachment.id !== newAttachment.id;
        })
      )
        form.insertListItem('attachments', newAttachment);

      notifications.show({
        message: 'Příloha byla úspěšně nahrána.',
        color: 'green',
        icon: <CheckIcon style={{ width: rem(20), height: rem(20) }} />,
      });
    } catch (err) {
      displayError(err);
    }
  };

  const addSvgPath = `${subPath}/icons/add.svg`;
  return (
    <Card shadow="sm" padding="md" radius="md" withBorder>
      <Group justify="space-between">
        <Group>
          <Text fw={700} size="xl" className="ml-2">
            Přílohy
          </Text>
          <Select
            radius="md"
            variant="filled"
            placeholder="Typ přílohy"
            ml={5}
            clearable={true}
            data={filterByFileTypeOptions}
            style={{
              width: '30%',
              backgroundColor: 'ButtonFace',
              borderRadius: '8px',
            }}
            onChange={value => {
              setFileTypeFilter(value ?? 'vse');
            }}
          />
          <Select
            radius="md"
            variant="filled"
            placeholder="Datum přidání"
            clearable={true}
            data={filterByDateOptions}
            style={{
              width: '30%',
              backgroundColor: 'ButtonFace',
              borderRadius: '8px',
            }}
            onChange={value => {
              setDateFilter(value ?? '');
            }}
          />
        </Group>
        <Menu
          transitionProps={{ transition: 'pop-bottom-left' }}
          position="bottom-end"
        >
          <Menu.Target>
            <UnstyledButton style={{ backgroundColor: 'transparent' }}>
              <img alt="add icon" src={addSvgPath}></img>
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown>
            {fileOptions.map(item => (
              <MenuItem
                key={item.value}
                onClick={() => {
                  setFileType(item.value);
                  handleFileUpload();
                }}
              >
                {item.label}
              </MenuItem>
            ))}
          </Menu.Dropdown>
        </Menu>
        <input
          data-testid="file-upload-input"
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </Group>
      <ScrollArea>{showTable()}</ScrollArea>
    </Card>
  );
};
